import { Box, styled, type BoxProps } from '@mui/material';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { MouseEvent, useRef, useState } from 'react';

import {
  ENOPOrganType,
  EResourceStatus,
  EResourceType,
  ICONS,
  isDefined,
} from '#shared';

import {
  CellPopper,
  UnifiedResourceDetails,
  type GroupedDrawerData,
} from '../../ResourceDrawer';

import type {
  HubReportStatus,
  HumanResource,
  OrganDetail,
  TResourceType,
} from '#entities';

const teamIconResourceMap: Record<EResourceType, TResourceType> = {
  Specialist: 'Specialist',
  Device: 'Device',
  Surgeon: 'Surgeon',
};

interface TableCellProps extends Omit<BoxProps, 'onClick'> {
  day: 'today' | 'tomorrow';
  details: OrganDetail;
  resourceType: EResourceType;
  organ: ENOPOrganType;
  location: HubReportStatus['location'];
  groupedDrawerData: GroupedDrawerData;
  trial?: boolean;
}

const PREFIX = 'TSMED-HubsReport-TableCell';
const tableCellClasses = {
  cell: `${PREFIX}-cell`,
  amount: `${PREFIX}-amount`,
  time: `${PREFIX}-time`,
  unavailable: `${PREFIX}-unavailable`,
};

type RootProps = { trial: boolean };
const excludedProps: PropertyKey[] = ['trial'];

const Root = styled(Box, {
  shouldForwardProp: p => !excludedProps.includes(p),
})<RootProps>(({ theme: { utils }, trial }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingBlock: 4,
  justifyContent: 'space-between',
  cursor: 'pointer',
  backgroundColor: trial ? '#FCE96A1A' : 'inherit',

  [`.${tableCellClasses.amount}`]: {
    fontSize: utils.clamp(14, 24),
    color: trial ? '#FCE96A' : '#b6b6b6',
    textAlign: 'center',
  },
  [`.${tableCellClasses.unavailable}`]: {
    color: '#ff4848',
  },
  [`.${tableCellClasses.time}`]: {
    fontSize: utils.clamp(10, 12),
    color: '#87888b',
    textAlign: 'center',
  },
}));

export const TableCell = ({
  details: { resources },
  day,
  organ,
  resourceType,
  location,
  groupedDrawerData,
  ...rest
}: TableCellProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };
  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
  };

  const enterTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseOver = (event: MouseEvent<HTMLElement>) => {
    enterTimeoutRef.current = setTimeout(() => {
      setAnchorEl(event.target as HTMLElement);
    }, 250);
  };
  const handleMouseOut = () => {
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
    }
    setAnchorEl(null);
  };

  const available = resources.filter(r => {
    if (resourceType === EResourceType.Device) {
      return r.status === EResourceStatus.Available;
    }
    // For human resources, check the current day's status
    return (r as HumanResource)[day]?.status === EResourceStatus.Available;
  });

  // Count trial resources based on resource type from available resources only
  const availableTrial = available.filter(r => {
    if (resourceType === EResourceType.Device) {
      // Device: check if trialProgram exists and matches current organ
      return (
        'trialProgram' in r &&
        isDefined(r.trialProgram) &&
        r.trialProgram.organ === organ
      );
    }
    if (resourceType === EResourceType.Specialist) {
      // Specialist: check if trialPrograms array has entries matching current organ
      return (
        'trialPrograms' in r &&
        Array.isArray(r.trialPrograms) &&
        r.trialPrograms.some(program => program.organ === organ)
      );
    }
    // Surgeon: skip (return false)
    return false;
  });

  const assigned = (resources as HumanResource[]).filter(
    r =>
      (resourceType === EResourceType.Device &&
        r.status === EResourceStatus.Assigned) ||
      r[day]?.status === EResourceStatus.Assigned,
  );

  const returning = (resources as HumanResource[])
    .filter(r => r[day] && r[day]?.status === EResourceStatus.OnRest)
    ?.sort((a, b) =>
      (a[day]?.endUTCDateTime ?? '') < (b[day]?.endUTCDateTime ?? '') ? -1 : 1,
    );

  const availableTotalCount = available.length ?? 0;
  const availableTrialCount = availableTrial.length ?? 0;
  const availableNonTrialCount = availableTotalCount - availableTrialCount;
  const assignedCount = assigned.length ?? 0;
  const returningCount = returning.length ?? 0;

  // Trial color only applies when there are available trial resources;
  // assigned or on-rest trial resources are ignored.
  const trialColor = availableTrialCount > 0;

  const returningTime = returning?.[0]?.[day]?.endUTCDateTime
    ? dayjs
        .utc(returning?.[0]?.[day]?.endUTCDateTime)
        .tz(returning[0][day].hubLocation.timezone)
        .format('HH:mm')
    : '-';
  const timeZoneAbbreviation = dayjs().tz(location.timezone).format('z');
  return (
    <>
      <Root
        {...rest}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        onClick={handleDrawerOpen}
        trial={trialColor}
      >
        <Box
          className={classnames(tableCellClasses.amount, {
            [tableCellClasses.unavailable]:
              !availableTotalCount && assignedCount,
          })}
        >
          {availableTotalCount || assignedCount || '-'}
        </Box>
        <Box className={tableCellClasses.time}>{returningTime}</Box>
      </Root>
      <CellPopper
        anchorEl={anchorEl}
        assignedCount={assignedCount}
        availableCount={availableTotalCount}
        availableTrialCount={availableTrialCount}
        availableNonTrialCount={availableNonTrialCount}
        returningCount={returningCount}
        day={day}
        icon={ICONS[resourceType]}
        primaryText={`${ENOPOrganType[organ]} ${teamIconResourceMap[resourceType]}`}
        secondaryText={`${location.city}, ${timeZoneAbbreviation}`}
      />
      <UnifiedResourceDetails
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        data={groupedDrawerData}
        location={location}
        day={day}
        organ={organ}
        resourceType={resourceType}
      />
    </>
  );
};
